import { Extension } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import type { EditorState } from "@tiptap/pm/state";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import {
  isTemplateActive,
  keysConditionState,
  subscribeConditionRefresh,
  type ConditionState,
} from "./conditional-evaluator";
import { collectReferencedKeys } from "./resolution";

// Re-exported for back-compat with existing imports (`setConditionEvaluator` is the
// single entry point the provider calls to drive both row and cell decorations).
export { setConditionEvaluator } from "./conditional-evaluator";
/** @deprecated use {@link ConditionState}; kept as an alias for existing imports. */
export type RowConditionState = ConditionState;

/**
 * Conditional table rows. The backend lifts a conditional that wraps a whole table
 * row (`{{#if …}}| … |{{/if}}`) onto the `tableRow` itself as a `condition`
 * attribute (see `backend/.../tiptap-markdown`). This extension:
 *
 *  1. Declares that `condition` attribute on the editor's `tableRow` schema so it
 *     survives the round-trip through the editor / Yjs (otherwise ProseMirror would
 *     strip the unknown attribute and the baked snapshot would lose the condition).
 *  2. Decorates conditional rows: in a read-only / frozen preview a row whose
 *     condition is false is hidden entirely (matching the baked snapshot, where the
 *     whole row is dropped); in the editable editor it is badged so authors can see
 *     it is conditional, and dimmed while currently inactive.
 *
 * Conditions are evaluated with the same resolver the dynamic nodes use, published
 * here by `DynamicNodeEditorProvider` via `setConditionEvaluator`.
 */

const TABLE_ROW_CONDITION_ATTR = "condition";

const conditionalRowPluginKey = new PluginKey("conditionalTableRows");

/** Evaluate a row condition to a boolean; defaults to visible when unknown. */
const isConditionActive = (condition: string): boolean =>
  isTemplateActive(`{{#if ${condition}}}1{{/if}}`);

/** Resolution state for the bar colour; defaults to `empty` (plain bar) when unknown. */
const conditionState = (condition: string): ConditionState =>
  keysConditionState(collectReferencedKeys(`{{#if ${condition}}}{{/if}}`));

const buildDecorations = (
  doc: ProseMirrorNode,
  badgeRows: boolean
): DecorationSet => {
  const decorations: Decoration[] = [];
  doc.descendants((node, pos) => {
    if (node.type.name !== "tableRow") {
      return;
    }
    const condition = node.attrs[TABLE_ROW_CONDITION_ATTR] as string | null;
    if (!condition) {
      return;
    }
    const active = isConditionActive(condition);
    const classes: string[] = [];
    if (badgeRows) {
      classes.push("formly-conditional-row");
      // Colour the bar by resolution (green/yellow/red), like the chip; dim the row
      // when the condition currently evaluates false (still present, just inactive).
      const state = conditionState(condition);
      if (state !== "empty") {
        classes.push(`formly-conditional-row--${state}`);
      }
      if (!active) {
        classes.push("formly-conditional-row--inactive");
      }
    } else if (!active) {
      // Read-only / frozen preview: hide the false row entirely.
      classes.push("formly-conditional-row--hidden");
    }
    if (classes.length > 0) {
      decorations.push(
        Decoration.node(pos, pos + node.nodeSize, { class: classes.join(" ") })
      );
    }
  });
  return DecorationSet.create(doc, decorations);
};

export interface ConditionalTableRowOptions {
  /**
   * Also badge/dim conditional rows when the editor is read-only, instead of
   * the default read-only behaviour of silently hiding false rows. Used by the
   * frames template preview, which is read-only but should look like the
   * authoring view (stripe coloured by resolution, false rows revealable).
   */
  decorateReadOnly: boolean;
}

export const ConditionalTableRow = Extension.create<ConditionalTableRowOptions>(
  {
    name: "conditionalTableRow",

    addOptions() {
      return {
        decorateReadOnly: false,
      };
    },

    addGlobalAttributes() {
      return [
        {
          types: ["tableRow"],
          attributes: {
            [TABLE_ROW_CONDITION_ATTR]: {
              default: null,
              parseHTML: (element) =>
                element.getAttribute("data-condition") || null,
              renderHTML: (attributes) =>
                attributes[TABLE_ROW_CONDITION_ATTR]
                  ? {
                      "data-condition": attributes[TABLE_ROW_CONDITION_ATTR],
                    }
                  : {},
            },
          },
        },
      ];
    },

    addProseMirrorPlugins() {
      const editor = this.editor;
      const { decorateReadOnly } = this.options;
      const badgeRows = () => editor.isEditable || decorateReadOnly;
      return [
        new Plugin({
          key: conditionalRowPluginKey,
          state: {
            init: (_, state: EditorState) =>
              buildDecorations(state.doc, badgeRows()),
            apply(tr, old, _oldState, newState) {
              if (tr.docChanged || tr.getMeta(conditionalRowPluginKey)) {
                return buildDecorations(newState.doc, badgeRows());
              }
              return old.map(tr.mapping, tr.doc);
            },
          },
          props: {
            decorations(state) {
              return conditionalRowPluginKey.getState(state);
            },
          },
          view: (view) => {
            // Recompute decorations when resolved data lands (out-of-band of any
            // document change), via the provider's `setConditionEvaluator`.
            const unsubscribe = subscribeConditionRefresh((): void => {
              try {
                view.dispatch(
                  view.state.tr.setMeta(conditionalRowPluginKey, true)
                );
              } catch {
                // The view may be tearing down; ignore.
              }
            });
            return {
              destroy() {
                unsubscribe();
              },
            };
          },
        }),
      ];
    },
  }
);

export default ConditionalTableRow;
